// Custom React hook for managing TitlePoint transaction history
import { useState, useEffect, useCallback } from 'react';
import {
  loadTransactionHistory,
  saveTransaction as saveTransactionApi,
  updateTransactionStatus as updateTransactionStatusApi,
  checkTransactionStatus as checkTransactionStatusApi
} from '../services/titlepointTransactions';

export const useTitlePointHistory = () => {
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pollingTransactions, setPollingTransactions] = useState({});

  // Load history on mount
  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const transactions = await loadTransactionHistory();
      setTransactionHistory(transactions);
    } catch (error) {
      console.error('Failed to load TitlePoint transaction history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const saveTransaction = useCallback(async (transaction) => {
    try {
      await saveTransactionApi(transaction);
      await loadHistory();
    } catch (error) {
      console.error('Failed to save TitlePoint transaction:', error);
      throw error;
    }
  }, [loadHistory]);

  const updateTransactionStatus = useCallback(async (requestId, statusData) => {
    try {
      await updateTransactionStatusApi(requestId, statusData);
      await loadHistory();
    } catch (error) {
      console.error('Failed to update TitlePoint transaction:', error);
      throw error;
    }
  }, [loadHistory]);

  const checkStatus = useCallback(async (requestId) => {
    try {
      setPollingTransactions(prev => ({ ...prev, [requestId]: 'loading' }));

      const statusResult = await checkTransactionStatusApi(requestId);

      // Update persistent storage
      await updateTransactionStatus(requestId, {
        status: statusResult.status,
        statusResponse: statusResult.fullResponse,
        lastStatusCheck: statusResult.rawResponse,
        lastChecked: statusResult.timestamp
      });

      setPollingTransactions(prev => ({ ...prev, [requestId]: statusResult.status || 'checked' }));

      return statusResult;
    } catch (error) {
      setPollingTransactions(prev => ({ ...prev, [requestId]: 'error' }));
      throw error;
    }
  }, [updateTransactionStatus]);

  const refreshHistory = loadHistory;

  return {
    transactionHistory,
    loadingHistory,
    pollingTransactions,
    loadHistory,
    saveTransaction,
    updateTransactionStatus,
    checkStatus,
    refreshHistory
  };
};

export default useTitlePointHistory;
